import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { generateRedirectCode } from '../../../../../../adapters';
import { FirebaseAuth } from '../../../../../../adapters/provider';
import { IPolicy } from '../../../../../../redux/slices/PolicyListSlice/types';
import { SEGMENT_ACTIONS } from '../../../../../../utils/constants/SegmentActionConstants';
import { FlagsmithFlags, useFeatureFlag } from '../../../../../../utils/flagsmith';
import useSegment from '../../../../../../utils/hooks/useSegment';
import { captureError } from '../../../../../../utils/observability';
import { resetState as resetEnrolmentData } from '../../../../../../redux/slices/EnrolmentSlice';
import { resetModularState } from '../../../../../../redux/slices/ModularEnrolmentSlice';
import { resetState as resetPolicyData } from '../../../../../../redux/slices/PolicyListSlice';
import { MAIN_ROUTES } from '../../../../../../utils/constants';
import { logoutUser as resetUserData } from '../../../../LoginContainer/redux/slices/UserSlice';
import { useHistory } from 'react-router-dom';

const useModularRedirect = (policies: IPolicy[], isLoading: boolean): void => {
    const isRedirectEnabled = useFeatureFlag(FlagsmithFlags.MODULAR_REDIRECT);
    const BENEFITS_PORTAL_URL = process.env.REACT_APP_BENEFITS_PORTAL_URL;
    const dispatch = useDispatch();
    const trackClick = useSegment('click');
    const trackLogout = useSegment('identify');
    const history = useHistory();

    const logout = () => {
        trackClick(SEGMENT_ACTIONS.CLICK.NAVIGATION_HEADER_SIGNOUT);
        FirebaseAuth.signOut();
        trackLogout({ name: SEGMENT_ACTIONS.IDENTIFY.USER_LOGOUT.name });
        dispatch(resetEnrolmentData());
        dispatch(resetUserData());
        dispatch(resetPolicyData());
        dispatch(resetModularState());
        history.replace(MAIN_ROUTES.login, { hideBanner: true });
    };

    const handleRedirection = async () => {
        try {
            const { id } = await generateRedirectCode();
            window.location.replace(`${BENEFITS_PORTAL_URL}/login?id=${id}`);
            logout();
        } catch {
            captureError(new Error('Failed to generate redirect code'), { source: 'modularRedirect.generateCode' });
        }
    };

    useEffect(() => {
        const isSameDomain = window.location.origin === BENEFITS_PORTAL_URL;
        if (!isRedirectEnabled || isLoading || !policies.length || !BENEFITS_PORTAL_URL || isSameDomain) return;
        const hasModularPolicy = policies.some((p) => p.policySecondaryType === 'MODULAR');
        const hasDealPolicy = policies.some((p) => !!p.dealId);
        if (hasModularPolicy || hasDealPolicy) handleRedirection();
    }, [isLoading, isRedirectEnabled, policies]);
};

export default useModularRedirect;
